import { features, useSelector, useThunkDispatch } from '@tunasong/redux'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { Button } from '../primitives/button.js'
import { Snackbar } from '../primitives/snackbar.js'

export interface ActionToast {
  message: string
  actionLabel?: string
  onAction?: () => void
  /**
   * ms; 6000 is the Undo window (request-access spec §8.4). `null` keeps the toast until it is dismissed, for an
   * action that is the only way back: a timeout on it fails WCAG 2.2.1 (GH #2453).
   */
  duration?: number | null
  /** An error is an assertive alert (the #2407 live-region rule); everything else stays a polite status */
  severity?: 'error'
}

interface OpenToast {
  toast: ActionToast
  /** The global alert that was showing when this toast opened; only a different one closes it */
  alertAtOpen: unknown
}

/**
 * A snackbar whose action runs a callback, for Undo and Retry. The global NotificationAlert's actions can only
 * navigate, and a callback must not be stored in redux, so this renders locally.
 *
 * One toast at a time, newest wins (request-access spec §8.9 #5): opening dismisses the global alert, and a new
 * global alert closes this toast.
 */
export const useActionToast = () => {
  const dispatch = useThunkDispatch()
  const alert = useSelector(state => state.notifications.alert)
  const [current, setCurrent] = useState<OpenToast | null>(null)
  /** A timed toast that arrived while a persistent one was showing; it shows once that one closes (GH #2505) */
  const [waiting, setWaiting] = useState<ActionToast | null>(null)

  const close = () => {
    setCurrent(waiting ? { toast: waiting, alertAtOpen: alert } : null)
    setWaiting(null)
  }

  /**
   * Newest wins, with one exception: a persistent toast (duration null) is never replaced by a timed one. Its action
   * is the only way back, as with a failed comment's Retry, so the timed one waits for it to close (GH #2505).
   */
  const showToast = (toast: ActionToast) => {
    if (current?.toast.duration === null && toast.duration !== null) {
      setWaiting(toast)
      return
    }
    setCurrent({ toast, alertAtOpen: alert })
    setWaiting(null)
    dispatch(features.notifications.actions.setAlert(null))
  }

  const supersededByAlert = Boolean(current && alert && alert !== current.alertAtOpen)
  useEffect(() => {
    if (supersededByAlert) {
      setCurrent(null)
      /** A newer global alert supersedes the waiting toast too */
      setWaiting(null)
    }
  }, [supersededByAlert])

  const handleAction = () => {
    const action = current?.toast.onAction
    close()
    action?.()
  }

  const duration = current?.toast.duration === undefined ? 6000 : current.toast.duration
  const isError = current?.toast.severity === 'error'

  const toast: ReactNode =
    current && !supersededByAlert ? (
      <Snackbar
        open
        onClose={close}
        autoHideDuration={duration}
        message={current.toast.message}
        /** Only an error overrides the Snackbar's polite status: an undefined role would remove it */
        {...(isError ? { role: 'alert', 'aria-live': 'assertive' as const } : {})}
        action={
          <>
            {current.toast.actionLabel ? (
              <Button size="small" onClick={handleAction}>
                {current.toast.actionLabel}
              </Button>
            ) : null}
            {/* With no timer, Dismiss is the only way to close it */}
            {duration === null ? (
              <Button size="small" onClick={close}>
                Dismiss
              </Button>
            ) : null}
          </>
        }
      />
    ) : null

  return { showToast, toast }
}
